import { api } from '../api'
import type { IChangeOrderTruckRequestData, ICurrentOrder, IOrder, IOrderDetail, IOrderTruck, IPreorderTime, ITable, ServerResponse, TAnalytics } from '../../models/models'

export const ordersApi = api.injectEndpoints({
	endpoints: (builder) => ({
		getAllOrders: builder.query<ITable<IOrder>, any>({
			query: (params) => ({
				url: 'orders',
				params,
			}),
			providesTags: ['orders'],
			transformResponse: (response: ServerResponse<ITable<IOrder>>) => response.data,
		}),
		getOrders: builder.query<ITable<IOrder>, any>({
			query: (params) => ({
				url: 'orders',
				params: {
					...params,
					preOrder: 0,
				},
			}),
			providesTags: ['orders'],
			transformResponse: (response: ServerResponse<ITable<IOrder>>) => response.data,
		}),
		getPreOrders: builder.query<ITable<IOrder>, any>({
			query: (params) => ({
				url: 'orders',
				params: {
					...params,
					preOrder: 1,
				},
			}),
			providesTags: ['orders'],
			transformResponse: (response: ServerResponse<ITable<IOrder>>) => response.data,
		}),
		getCompletedOrders: builder.query<ITable<IOrder>, any>({
			query: (params) => ({
				url: 'orders',
				params: {
					...params,
					completed: 1,
				},
			}),
			providesTags: ['orders'],
			transformResponse: (response: ServerResponse<ITable<IOrder>>) => response.data,
		}),
		changeVisibleOrders: builder.mutation<ServerResponse<ITable<any>>, any>({
			query: (body) => ({
				url: 'orders/view',
				method: 'PUT',
				body,
			}),
		}),
		getOrderById: builder.query<IOrderDetail, any>({
			query: ({ id }) => ({
				url: `orders/${id}`,
			}),
			providesTags: ['order'],
			transformResponse: (response: ServerResponse<IOrderDetail>) => response.data,
		}),
		getOrderTrucks: builder.query<IOrderTruck[], { id: string | number; showAll: boolean }>({
			query: ({ id, showAll }) => ({
				url: `orders/${id}/trucks`,
				params: {
					showAll: Number(showAll),
				},
			}),
			transformResponse: (response: ServerResponse<IOrderTruck[]>) => response.data,
		}),
		getPreorderTime: builder.query<IPreorderTime[], any>({
			query: () => ({
				url: `${process.env.REACT_APP_API_URL?.replace('remote-control-panel/', '')}pre_order_time`,
				params: {
					timeZone: 'Europe/Kyiv',
				},
			}),
			transformResponse: (response: ServerResponse<IPreorderTime[]>) => response.data,
		}),
		updateOrderTruck: builder.mutation<ServerResponse<any>, { id: string | number; data: IChangeOrderTruckRequestData }>({
			query: (body) => ({
				url: `orders/${body.id}/change_truck`,
				method: 'POST',
				body: body.data,
			}),
			invalidatesTags: ['dashboard', 'order'],
		}),
		changeExciseInvoiceNumber: builder.mutation<ServerResponse<any>, { id: number; number: number }>({
			query: (body) => ({
				url: `orders/addresses/${body.id}`,
				method: 'PUT',
				body: {
					exciseInvoiceNumber: body.number,
				},
			}),
			invalidatesTags: ['order'],
		}),
		cancelOrder: builder.mutation<ServerResponse<any>, { id: string | number }>({
			query: ({ id }) => ({
				url: `orders/${id}/cancel`,
				method: 'POST',
			}),
			invalidatesTags: ['orders', 'order', 'dashboard'],
		}),
		getCurrentOrders: builder.query<ICurrentOrder[], { regionId: string }>({
			query: (params) => ({
				url: 'orders/current',
				params,
			}),
			providesTags: ['orders', 'currentOrders'],
			transformResponse: (response: ServerResponse<ICurrentOrder[]>) => response.data,
		}),
		suspendOrder: builder.mutation<ServerResponse<any>, { id: string | number }>({
			query: ({ id }) => ({
				url: `orders/${id}/suspend`,
				method: 'POST',
			}),
			invalidatesTags: ['orders', 'currentOrders', 'dashboard'],
		}),
		getAnalytics: builder.query<ITable<TAnalytics>, any>({
			query: (params) => ({
				url: 'orders/completed',
				params,
			}),
			providesTags: ['analytics'],
			transformResponse: (response: ServerResponse<ITable<TAnalytics>>) => ({
				...response.data,
				data: response.data.data.map((i) => ({ ...i, entityId: i.id })),
			}),
		}),
	}),
})

export const {
	useGetAllOrdersQuery,
	useGetOrdersQuery,
	useGetPreOrdersQuery,
	useGetCompletedOrdersQuery,
	useChangeVisibleOrdersMutation,
	useGetOrderByIdQuery,
	useGetOrderTrucksQuery,
	useGetPreorderTimeQuery,
	useUpdateOrderTruckMutation,
	useChangeExciseInvoiceNumberMutation,
	useCancelOrderMutation,
	useGetCurrentOrdersQuery,
	useSuspendOrderMutation,
	useGetAnalyticsQuery
} = ordersApi
